import React, { useState, useEffect, useCallback } from "react";
import { Password } from "primereact/password";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { linearService } from "../services/linearService";

interface LinearSettingsProps {
  onConfigured: () => void;
}

export const LinearSettings: React.FC<LinearSettingsProps> = ({
  onConfigured,
}) => {
  const [apiKey, setApiKey] = useState("");
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isConfigured, setIsConfigured] = useState(false);
  const [userName, setUserName] = useState<string | null>(null);

  const configureAndValidate = useCallback(
    async (key: string) => {
      setValidating(true);
      setError(null);
      try {
        linearService.configure(key);
        const user = await linearService.validateConnection();
        setUserName(user.name);
        setIsConfigured(true);
        localStorage.setItem("linear-api-key", key);
        onConfigured();
      } catch {
        setError("Failed to connect to Linear. Please check your API key.");
        setIsConfigured(false);
      } finally {
        setValidating(false);
      }
    },
    [onConfigured]
  );

  useEffect(() => {
    const savedKey = localStorage.getItem("linear-api-key");
    if (savedKey) {
      setApiKey(savedKey);
      configureAndValidate(savedKey);
    }
  }, [configureAndValidate]);

  const handleDisconnect = () => {
    localStorage.removeItem("linear-api-key");
    setIsConfigured(false);
    setUserName(null);
    setApiKey("");
  };

  if (isConfigured) {
    return (
      <div className="flex align-items-center gap-3">
        <Message severity="success" text={`Connected as ${userName}`} />
        <Button
          label="Disconnect"
          icon="pi pi-sign-out"
          severity="secondary"
          size="small"
          onClick={handleDisconnect}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-column gap-3">
      <div className="text-sm text-500 mb-2">
        Connect to Linear to fetch completed issues ready for release. Generate
        a personal API key in{" "}
        <a
          href="https://linear.app/settings/api"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary"
        >
          Linear Settings → API
        </a>
        .
      </div>

      <div className="flex align-items-end gap-3 flex-wrap">
        <div className="flex flex-column gap-2">
          <label htmlFor="linear-api-key" className="text-sm">
            API Key
          </label>
          <Password
            inputId="linear-api-key"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && apiKey && configureAndValidate(apiKey)}
            placeholder="lin_api_..."
            toggleMask
            feedback={false}
            style={{ width: "300px" }}
          />
        </div>

        <Button
          label="Connect"
          icon="pi pi-link"
          onClick={() => configureAndValidate(apiKey)}
          loading={validating}
          disabled={!apiKey}
        />
      </div>

      {error && <Message severity="error" text={error} />}
    </div>
  );
};
